import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import { maskCurrency } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";
import FormLabel from "../../components/FormLabel";
import usePermission from "../../hooks/usePermission";
import PrevisaoPagamentos from "../../components/PrevisaoPagamentos";


const CadastrarOrcamentoPrestador = ({ orcamentoId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(orcamentoId);
    const podeDeletar = usePermission("DELETAR_ORCAMENTO_PRESTADOR_OBRA");

    // Modais de confirmação/sucesso
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [pagamentos, setPagamentos] = useState([]);


    // Listas para dropdowns
    const [obras, setObras] = useState([]);
    const [prestadores, setPrestadores] = useState([]);

    // Estado do formulário
    const [formData, setFormData] = useState({
        valorCobrado: "",
        descricao: "",
        dataEnvio: "",
        nomeResponsavel: "",
        obraId: "",
        prestadorId: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Supõe que existem endpoints para buscar as obras e prestadores
                const [obrasRes, prestadoresRes] = await Promise.all([
                    api.get("/obras/listar/todas"),
                    api.get("/fornecedores/prestadores/todos"),
                ]);
                setObras(obrasRes.data);
                setPrestadores(prestadoresRes.data);
            } catch (error) {
                console.error("Erro ao carregar obras ou prestadores:", error);
            }
        };

        fetchData();

        if (isEditMode) {
            const fetchOrcamento = async () => {
                try {
                    const response = await api.get(`/orcamentosprestador/${orcamentoId}`);
                    const data = response.data;
                    setFormData({
                        valorCobrado: data.valor ? maskCurrency(Math.round(data.valor * 100)) : "",
                        descricao: data.descricao || "",
                        dataEnvio: data.dataEnvio ? data.dataEnvio.split("T")[0] : "",
                        nomeResponsavel: data.nomeResponsavel || "",
                        obraId: data.obra?.id || "",
                        prestadorId: data.fornecedor?.id || "",
                    });

                    // Popula os pagamentos já existentes
                    if (data.pagamentos && Array.isArray(data.pagamentos)) {
                        setPagamentos(
                            data.pagamentos.map((p, idx) => ({
                                ordem: idx + 1,
                                valor: Number(p.valorParcela).toFixed(2),
                                dataVencimento: p.dataVencimento.split("T")[0],
                                formaPagamento: p.formaPagamento.toUpperCase() || "",
                                status: p.statusId === 19 ? "Pago" : "Pendente", // ajuste conforme seus IDs de status
                                dataPagamento: p.dataPagamento
                                    ? p.dataPagamento.split("T")[0]
                                    : "",
                                observacao: p.observacao || "",
                            }))
                        );
                    }
                } catch (error) {
                    console.error("Erro ao carregar orçamento:", error);
                }
            };
            fetchOrcamento();
        }
    }, [orcamentoId, isEditMode]);

    // Handlers para inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCurrencyChange = (fieldName) => (e) => {
        const masked = maskCurrency(e.target.value);
        setFormData((prev) => ({ ...prev, [fieldName]: masked }));
    };

    // Submissão do formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirmDialog(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            const dataToSend = {
                valorCobrado: parseCurrencyToFloat(formData.valorCobrado),
                descricao: formData.descricao,
                dataEnvio: formData.dataEnvio,
                nomeResponsavel: formData.nomeResponsavel,
                obraId: formData.obraId,
                prestadorId: formData.prestadorId,
                pagamentos: pagamentos.map((p) => ({
                    valorParcela: parseFloat(p.valor), // já está .toFixed(2) no PrevisaoPagamentos
                    dataVencimento: p.dataVencimento,
                    formaPagamento: p.formaPagamento.toUpperCase(),
                    // Faça o mapeamento correto do status:
                    statusId: p.status === "Pago" ? 19 : 18, // ou ajuste conforme IDs do seu sistema
                    dataPagamento: p.dataPagamento || null,
                    observacao: p.observacao || null,
                })),
            };

            let response;
            if (isEditMode) {
                response = await api.put(`/orcamentosprestador/${orcamentoId}`, dataToSend);
            } else {
                response = await api.post("/orcamentosprestador", dataToSend);
            }

            if (response.status === 200 || response.status === 201) {
                setShowSuccessDialog(true);
                setTimeout(() => {
                    setShowSuccessDialog(false);
                    navigate("/orcamentosprestadores");
                }, 3000);
            } else {
                throw new Error("Erro ao salvar orçamento");
            }
        } catch (error) {
            console.error(
                `Erro ao ${isEditMode ? "atualizar" : "cadastrar"} orçamento:`,
                error
            );
            alert("Ocorreu um erro. Verifique os dados e tente novamente.");
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    const handleBack = () => {
        navigate("/orcamentosprestadores");
    };

    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/orcamentosprestador/${orcamentoId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/orcamentosprestadores");
        } catch (error) {
            console.error("Erro ao remover orçamento:", error);
            alert("Erro ao remover orçamento.");
        }
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Orçamento" : "Cadastrar Orçamento"}
                    </h1>
                </div>

                {/* Informações do Orçamento */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">
                        Informações do Orçamento
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {/* Valor Cobrado */}
                        <div className="flex flex-col">
                            <FormLabel htmlFor="valorCobrado" isRequired={true}>
                                Valor Cobrado (R$)
                            </FormLabel>
                            <input
                                type="text"
                                name="valorCobrado"
                                value={formData.valorCobrado}
                                onChange={handleCurrencyChange("valorCobrado")}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            />
                        </div>

                        {/* Data de Envio */}
                        <div className="flex flex-col">
                            <FormLabel htmlFor="dataEnvio" isRequired={true}>
                                Data de Envio
                            </FormLabel>
                            <input
                                type="date"
                                name="dataEnvio"
                                value={formData.dataEnvio}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            />
                        </div>

                        {/* Nome do Responsável */}
                        <div className="flex flex-col">
                            <FormLabel htmlFor="nomeResponsavel" isRequired={true}>
                                Nome do Responsável
                            </FormLabel>
                            <input
                                type="text"
                                name="nomeResponsavel"
                                value={formData.nomeResponsavel}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            />
                        </div>
                    </div>

                    {/* Descrição */}
                    <div className="grid grid-cols-1 gap-4">
                        <div className="flex flex-col">
                            <FormLabel htmlFor="descricao">Descrição do Orçamento</FormLabel>
                            <textarea
                                name="descricao"
                                value={formData.descricao}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                rows={4}
                            />
                        </div>
                    </div>
                </div>

                {/* Relacionamentos */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">
                        Relacionamentos
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Obra */}
                        <div className="flex flex-col">
                            <FormLabel htmlFor="obraId" isRequired={true}>
                                Obra
                            </FormLabel>
                            <select
                                name="obraId"
                                value={formData.obraId}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            >
                                <option value="">Selecionar</option>
                                {obras.map((obra) => (
                                    <option key={obra.id} value={obra.id}>
                                        {obra.nomeProjeto}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Prestador */}
                        <div className="flex flex-col">
                            <FormLabel htmlFor="prestadorId" isRequired={true}>
                                Prestador
                            </FormLabel>
                            <select
                                name="prestadorId"
                                value={formData.prestadorId}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            >
                                <option value="">Selecionar</option>
                                {prestadores.map((prestador) => (
                                    <option key={prestador.id} value={prestador.id}>
                                        {prestador.nomeFornecedor + " - " + prestador.cpfCnpj}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {isEditMode == true && (
                    <PrevisaoPagamentos
                        valorTotal={formData.valorCobrado} // OU o campo correto do orçamento
                        pagamentos={pagamentos}
                        setPagamentos={setPagamentos}
                        valorDefaultDataInicial={formData.dataInicio}
                    />
                )}

                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    {isEditMode && podeDeletar && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => setShowConfirmDeleteRegister(true)}
                        >
                            <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                            <span>Apagar</span>
                        </button>
                    )}

                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        >
                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                            <span>Salvar</span>
                        </button>
                    </div>
                </div>
            </form>

            {/* Modais */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelSubmit}
                message="Deseja salvar as informações?"
            />

            <SuccessModal
                isVisible={showSuccessDialog}
                message="Informações salvas com sucesso!"
                onClose={() => setShowSuccessDialog(false)}
            />

            <ConfirmationModal
                isVisible={showConfirmDeleteRegister}
                onConfirm={handleDeleteRegister}
                onCancel={() => setShowConfirmDeleteRegister(false)}
                message="Deseja apagar este registro?"
                textButtonConfirm="Sim"
            />
        </Template>
    );
};

export default CadastrarOrcamentoPrestador;
